import React, { useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Divider,
  CircularProgress,
  Alert,
  AlertTitle,
  Collapse,
  Chip,
  Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useWorkItemData } from '../hooks/useWorkItemData';
import { usePartOrderLinesByWorkitem } from '../hooks/usePartOrderLinesByWorkitem';
import { WorkItem } from './workitems/workItemTypes';
import { sortAssignees, sortWorkItemsForDashboard } from './workitems/utils/workItemUtils';
import { WorkItemAccordionRow } from './workitems/components/WorkItemAccordionRow';
import { TechnicianCardList, WorkItemPanelContainer } from './technician-card';
import { FEATURE_TECH_CARD_V2 } from '../features';
import { SecondaryToolbar, ToolbarSearch, ToolbarActionGroup, ToolbarSpacer, ToolbarDivider } from './toolbar';
import { DebugPanel } from './debug/DebugPanel';
import { installApiCallLogger } from '../services/apiCallLogger';
import type { DebugPanelConfig } from '../types/debugPanel';
import { NewInvoiceDialog } from './invoices/NewInvoiceDialog';
import { WorkItemChangeCustomerDialog } from './workitems/WorkItemChangeCustomerDialog';
import {
  getTaskListsCacheMetadata,
  invalidateTaskListsCache,
  refreshTaskListsCache,
} from '../services/taskListCacheService';
import { reorderWorkItems as reorderWorkItemsApi, reassignWorkItem as reassignWorkItemApi, updateWorkItemStatus, deleteWorkItem, searchWorkItemHistory } from '../services/workItemDataService';
import { cancelCheckInSession } from '../services/checkInApi';
import { CancelSessionDialog } from './check-in/sessionDetail/dialogs/CancelSessionDialog';
import { JobDeleteDialog } from './workitems/JobDeleteDialog';
import { computeReorder, IndexedItem, calculateMidpoint, DISPLAY_INDEX_GAP } from '../utils/gapIndexing';
import { watchJobStatusResult } from '../services/jobStatusWatcher';

installApiCallLogger();

/**
 * A change the orchestrator refused after the POST had already been accepted.
 * The heading is composed by the caller: a refused status change and a refused
 * reassign name different things — the status asked for, or the technician it
 * was handed to — and only the caller knows which it attempted.
 */
interface WorkItemActionError {
  heading: string;
  reason: string;
}

type ViewType = 'tickets' | 'workitems' | 'workitem-dashboard' | 'debug' | 'new-ticket' | 'edit-ticket' | 'new-check-in' | 'check-in-session' | 'ticket-sheet';

interface UserInfo {
  displayName: string;
  userPrincipalName: string;
  roles: string[];
}

interface WorkItemDashboardProps {
  onNavigate?: (view: ViewType) => void;
  userInfo?: UserInfo | null;
}

// TODO: Replace with auth context
const CURRENT_USER = 'Steve Thomas';

const isTicketNumber = (value: string) => /^8\d{6,7}$/.test(value.trim());

// How a refusal banner names the item. The RepairShopr ticket id is what the
// tech has in front of them; appointments may carry neither id nor number, so
// fall back to something readable rather than a bare "#".
const describeWorkItem = (item: WorkItem): string => {
  const ref = item.id || item.number?.toString() || '';
  return ref ? `#${ref}` : 'this ticket';
};

export const WorkItemDashboard: React.FC<WorkItemDashboardProps> = ({ onNavigate, userInfo }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [workItemSearch, setWorkItemSearch] = useState('');
  const [showResolvedToday, setShowResolvedToday] = useState(false);
  // "Devices with customers" — only the jobs whose device was released to the
  // customer while the job waits for parts (docs/device-release-awaiting-parts.md).
  const [showWithCustomer, setShowWithCustomer] = useState(false);
  const [resolvedTodayItems, setResolvedTodayItems] = useState<WorkItem[]>([]);
  const [newInvoiceOpen, setNewInvoiceOpen] = useState(false);
  // Set when the orchestrator reports that a status change or a reassign was
  // refused. Held until dismissed rather than auto-hidden: RepairShopr's reason
  // is usually an instruction ("complete the worksheet first"), not a
  // notification.
  const [actionError, setActionError] = useState<WorkItemActionError | null>(null);
  // Set when a tech opens "Change customer" from a job's expanded panel.
  const [changeCustomerTarget, setChangeCustomerTarget] = useState<WorkItem | null>(null);
  // Set when reception clicks Delete on a check-in row — opens a styled
  // cancel-reason dialog instead of using window.prompt.
  const [pendingCancelCheckIn, setPendingCancelCheckIn] = useState<{
    sessionGuid: string;
    workItemGuid: string;
  } | null>(null);
  // Set when Delete is chosen on a job — opens the delete dialog, which shows
  // what the delete takes with it before anything is removed.
  const [pendingDeleteJob, setPendingDeleteJob] = useState<WorkItem | null>(null);

  const handleWorkItemSearch = useCallback(() => {
    const term = workItemSearch.trim();
    if (!term) return;
    if (isTicketNumber(term)) {
      navigate(`/ticket/${term}`);
    } else {
      sessionStorage.setItem('workitem_search_term', term);
      setWorkItemSearch('');
      onNavigate?.('workitem-search' as ViewType);
    }
  }, [workItemSearch, navigate, onNavigate]);

  const {
    workItems,
    isLoading,
    error,
    refresh,
    updateWorkItem,
    removeWorkItem,
    restoreWorkItem,
    lockForReorder,
    changedGuids,
    markChanged,
  } = useWorkItemData();

  const { linesByWorkitem: partLinesByWorkitem } = usePartOrderLinesByWorkitem();

  // Track items resolved during this session — supplements the API fetch
  // so that items resolved moments ago appear immediately without waiting
  // for the next API round-trip.
  const resolvedTodayRef = useRef<Map<string, WorkItem>>(new Map());

  const fetchResolvedToday = useCallback(async () => {
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    try {
      const result = await searchWorkItemHistory({
        resolvedFrom: today,
        resolvedTo: today,
        pageSize: 100,
      });
      setResolvedTodayItems(result.items);
    } catch (err) {
      console.error('[Dashboard] Failed to fetch resolved items:', err);
    }
  }, []);

  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const toggleGroup = useCallback((assignee: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(assignee)) next.delete(assignee);
      else next.add(assignee);
      return next;
    });
  }, []);

  // Drag and drop state
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const prevOrderRef = useRef<WorkItem[] | null>(null);

  const handleDragStart = useCallback((e: React.DragEvent, workItemId: string) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', workItemId);
    // Defer state update to next frame — a synchronous re-render during
    // dragstart can mutate the DOM and cause the browser to cancel the drag.
    requestAnimationFrame(() => setDraggedId(workItemId));
  }, []);

  const handleDragOver = useCallback((_e: React.DragEvent, workItemId: string) => {
    setDragOverId(workItemId);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedId(null);
    setDragOverId(null);
  }, []);

  // Core move logic, shared by the legacy native-DnD handleDrop and the V2
  // @dnd-kit TechnicianCardList. `targetId` is the guid to insert the source
  // BEFORE, or an "end::{assignee}" sentinel to drop at the bottom of a group.
  const commitMove = useCallback((sourceId: string, targetId: string) => {
    if (!sourceId || sourceId === targetId) return;

    const sourceItem = workItems.find(w => w.guid === sourceId);
    if (!sourceItem) return;

    const sourceAssignee = sourceItem.userFullName || 'Unassigned';

    // "end::{assignee}" sentinel = drop at the bottom of a group
    const isEndDrop = targetId.startsWith('end::');
    const endDropAssignee = isEndDrop ? targetId.slice(5) : undefined;

    const targetItem = isEndDrop ? undefined : workItems.find(w => w.guid === targetId);
    if (!isEndDrop && !targetItem) return;

    const targetAssignee = isEndDrop ? endDropAssignee! : (targetItem!.userFullName || 'Unassigned');

    if (sourceAssignee !== targetAssignee) {
      // Cross-group reassignment — skip if target is Unassigned (no API support)
      if (targetAssignee === 'Unassigned') return;

      const prevFullName = sourceItem.userFullName;
      const prevEmail = sourceItem.userEmailAddress;
      const prevDisplayIndex = sourceItem.displayIndex;

      // Compute displayIndex for the target group
      const targetGroupItems: IndexedItem[] = workItems
        .filter(w => (w.userFullName || 'Unassigned') === targetAssignee)
        .sort((a, b) => (Number(a.displayIndex) || 0) - (Number(b.displayIndex) || 0))
        .map(w => ({ id: w.id, guid: w.guid, type: w.type, displayIndex: Number(w.displayIndex) || 0 }));

      let newDisplayIndex: number;
      let resolvedAssigneeFullName: string;
      let resolvedAssigneeEmail: string | undefined;

      if (isEndDrop) {
        // Drop at end — place after the last item
        const lastItem = targetGroupItems[targetGroupItems.length - 1];
        newDisplayIndex = calculateMidpoint(lastItem, undefined) ?? (targetGroupItems.length + 1) * DISPLAY_INDEX_GAP;
        // Resolve assignee info from any item in the target group
        const anyTargetItem = workItems.find(w => (w.userFullName || 'Unassigned') === targetAssignee);
        resolvedAssigneeFullName = anyTargetItem?.userFullName ?? targetAssignee;
        resolvedAssigneeEmail = anyTargetItem?.userEmailAddress;
      } else {
        // Drop on an item — insert before target
        const targetIdx = targetGroupItems.findIndex(item => item.guid === targetId);
        const prevItem = targetIdx > 0 ? targetGroupItems[targetIdx - 1] : undefined;
        const nextItem = targetGroupItems[targetIdx];
        newDisplayIndex = calculateMidpoint(prevItem, nextItem) ?? (targetIdx + 1) * DISPLAY_INDEX_GAP;
        resolvedAssigneeFullName = targetItem!.userFullName;
        resolvedAssigneeEmail = targetItem!.userEmailAddress;
      }

      // Suppress SignalR events while the reassign + reorder propagates through Dataverse
      lockForReorder();

      // Optimistic update
      updateWorkItem(sourceItem.guid, {
        userFullName: resolvedAssigneeFullName,
        userEmailAddress: resolvedAssigneeEmail,
        displayIndex: newDisplayIndex,
      });
      markChanged(sourceItem.guid);

      // Undo the optimistic move. Shared by the request failing outright and by
      // the orchestrator reporting a refusal later. It writes local state, so
      // it takes effect inside the reorder lock; nothing re-applies the failed
      // assignee afterwards because the orchestrator only writes the new
      // assignee to Dataverse *after* RepairShopr accepts, and only broadcasts
      // workitems:changed on success. (The displayIndex is patched up front by
      // sm4rt-api, so a later refresh may still show the moved position within
      // the original group — the assignee, which is what the row is read for,
      // is correct.)
      const revert = () => {
        updateWorkItem(sourceItem.guid, {
          userFullName: prevFullName,
          userEmailAddress: prevEmail,
          displayIndex: prevDisplayIndex,
        });
      };

      // Reassign + update displayIndex in Dataverse (DV patched before orchestrator message)
      reassignWorkItemApi(sourceItem.guid, sourceItem.id, resolvedAssigneeFullName, {
        displayIndex: newDisplayIndex,
        type: sourceItem.type,
      })
        .then(({ messageId }) => {
          if (!messageId) return;

          // The 202 only means "queued". RepairShopr can still refuse the
          // reassign — a locked ticket, a tech it may not be given — and that
          // refusal arrives on jobs:status long after the POST resolved. The
          // reorder lock doesn't reach this: it only suppresses
          // workitems:changed, a different subscription.
          watchJobStatusResult(messageId, {
            onFailure: (reason) => {
              console.error('[Dashboard] reassign rejected — reverting:', reason);
              revert();
              setActionError({
                heading: `Couldn’t reassign ${describeWorkItem(sourceItem)} to ${resolvedAssigneeFullName}`,
                reason,
              });
            },
          });
        })
        .catch(() => {
          revert();
        });
      return;
    }

    // Same-group reorder (gap-based indexing)
    const groupItems: IndexedItem[] = workItems
      .filter(w => (w.userFullName || 'Unassigned') === sourceAssignee)
      .sort((a, b) => (Number(a.displayIndex) || 0) - (Number(b.displayIndex) || 0))
      .map(w => ({ id: w.id, guid: w.guid, type: w.type, displayIndex: Number(w.displayIndex) || 0 }));

    // Save previous order for rollback
    prevOrderRef.current = workItems
      .filter(w => (w.userFullName || 'Unassigned') === sourceAssignee)
      .map(w => ({ ...w }));

    // Suppress SignalR events while the reorder propagates through Dataverse
    lockForReorder();

    if (isEndDrop) {
      // Drop at end of own group — place after the last item
      const withoutSource = groupItems.filter(item => item.guid !== sourceId);
      const lastItem = withoutSource[withoutSource.length - 1];
      const newIndex = calculateMidpoint(lastItem, undefined) ?? (withoutSource.length + 1) * DISPLAY_INDEX_GAP;

      updateWorkItem(sourceItem.guid, { displayIndex: newIndex });
      markChanged(sourceItem.guid);

      reorderWorkItemsApi([{ guid: sourceItem.guid, type: sourceItem.type, displayIndex: newIndex }])
        .catch((err) => {
          console.error('[Dashboard] reorderWorkItemsApi FAILED — reverting', err);
          if (prevOrderRef.current) {
            prevOrderRef.current.forEach(item => {
              updateWorkItem(item.guid, { displayIndex: item.displayIndex });
            });
          }
        });
      return;
    }

    console.log('[Dashboard] Reorder: source=', sourceId, '→ target=', targetId);
    console.log('[Dashboard] Group before:', groupItems.map(w => `${w.id}:idx${w.displayIndex}`));

    // Compute gap-based reorder (usually sends only the moved item)
    const { updates, localUpdates } = computeReorder(groupItems, sourceId, targetId);
    if (updates.length === 0) return;

    // Optimistic local state update
    localUpdates.forEach((newIndex, itemGuid) => {
      updateWorkItem(itemGuid, { displayIndex: newIndex });
    });
    markChanged(sourceItem.guid);

    // Call API, revert on failure
    console.log('[Dashboard] Calling reorderWorkItemsApi with', updates.length, 'items');
    reorderWorkItemsApi(updates)
      .then(() => console.log('[Dashboard] reorderWorkItemsApi succeeded'))
      .catch((err) => {
        console.error('[Dashboard] reorderWorkItemsApi FAILED — reverting', err);
        if (prevOrderRef.current) {
          prevOrderRef.current.forEach(item => {
            updateWorkItem(item.guid, { displayIndex: item.displayIndex });
          });
        }
      });
  }, [workItems, updateWorkItem, lockForReorder, markChanged]);

  // Reassign a work item to a chosen technician (from the drag-handle context
  // menu), placing it at the BOTTOM of the target group. Mirrors the cross-group
  // end-drop branch of commitMove. `assigneeEmail` is used only when the target
  // has no current tickets to resolve the email from.
  const reassignToAssignee = useCallback((sourceId: string, assigneeFullName: string, assigneeEmail?: string) => {
    const sourceItem = workItems.find(w => w.guid === sourceId);
    if (!sourceItem) return;

    const targetAssignee = assigneeFullName;
    // Reassigning to "Unassigned" isn't supported by the API.
    if (!targetAssignee || targetAssignee === 'Unassigned') return;
    // No-op if it's already assigned to this technician.
    if ((sourceItem.userFullName || 'Unassigned') === targetAssignee) return;

    const prevFullName = sourceItem.userFullName;
    const prevEmail = sourceItem.userEmailAddress;
    const prevDisplayIndex = sourceItem.displayIndex;

    // Place at the bottom of the target group.
    const targetGroupItems: IndexedItem[] = workItems
      .filter(w => (w.userFullName || 'Unassigned') === targetAssignee && w.guid !== sourceId)
      .sort((a, b) => (Number(a.displayIndex) || 0) - (Number(b.displayIndex) || 0))
      .map(w => ({ id: w.id, guid: w.guid, type: w.type, displayIndex: Number(w.displayIndex) || 0 }));

    const lastItem = targetGroupItems[targetGroupItems.length - 1];
    const newDisplayIndex = calculateMidpoint(lastItem, undefined) ?? (targetGroupItems.length + 1) * DISPLAY_INDEX_GAP;

    // Prefer an existing target-group item's email; fall back to the picked user's.
    const anyTargetItem = workItems.find(w => (w.userFullName || 'Unassigned') === targetAssignee);
    const resolvedEmail = anyTargetItem?.userEmailAddress ?? assigneeEmail;

    // Suppress SignalR events while the reassign propagates through Dataverse.
    lockForReorder();

    // Optimistic update
    updateWorkItem(sourceItem.guid, {
      userFullName: targetAssignee,
      userEmailAddress: resolvedEmail,
      displayIndex: newDisplayIndex,
    });
    markChanged(sourceItem.guid);

    // Same revert as the cross-group drag in commitMove — see the note there
    // on why a local write survives the reorder lock.
    const revert = () => {
      updateWorkItem(sourceItem.guid, {
        userFullName: prevFullName,
        userEmailAddress: prevEmail,
        displayIndex: prevDisplayIndex,
      });
    };

    reassignWorkItemApi(sourceItem.guid, sourceItem.id, targetAssignee, {
      displayIndex: newDisplayIndex,
      type: sourceItem.type,
    })
      .then(({ messageId }) => {
        if (!messageId) return;

        watchJobStatusResult(messageId, {
          onFailure: (reason) => {
            console.error('[Dashboard] reassign rejected — reverting:', reason);
            revert();
            setActionError({
              heading: `Couldn’t reassign ${describeWorkItem(sourceItem)} to ${targetAssignee}`,
              reason,
            });
          },
        });
      })
      .catch(() => {
        revert();
      });
  }, [workItems, updateWorkItem, lockForReorder, markChanged]);

  const handleDrop = useCallback((e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    const sourceId = draggedId;
    setDraggedId(null);
    setDragOverId(null);
    if (!sourceId) return;
    commitMove(sourceId, targetId);
  }, [draggedId, commitMove]);

  const handleStatusChange = useCallback((workItem: WorkItem, newStatus: string) => {
    const prevStatus = workItem.status;
    const wasResolved = ['resolved', 'closed'].includes((prevStatus ?? '').toLowerCase());
    const isResolved = ['resolved', 'closed'].includes(newStatus.toLowerCase());

    // Snapshot the item when it's being resolved so we can show it later
    if (isResolved) {
      resolvedTodayRef.current.set(workItem.guid, {
        ...workItem,
        status: newStatus,
        resolvedAt: new Date().toISOString(),
      });
      console.log('[Dashboard] Saved resolved snapshot for', workItem.guid, '— ref size:', resolvedTodayRef.current.size);
    }

    // Reopening a resolved item: the main work-item list filters out resolved
    // items, so an updateWorkItem alone is invisible. Clear the resolved-today
    // sources and inject the item back into the active list so the dashboard
    // reflects the new status optimistically.
    if (wasResolved && !isResolved) {
      resolvedTodayRef.current.delete(workItem.guid);
      setResolvedTodayItems(prev => prev.filter(w => w.guid !== workItem.guid));
      restoreWorkItem({
        ...workItem,
        status: newStatus,
        appointmentStatus: workItem.type !== 'job' ? newStatus : undefined,
        resolvedAt: undefined,
      });
    }

    // Use guid as the key — appointments may not have a RepairShopr ticket ID
    updateWorkItem(workItem.guid, { status: newStatus, appointmentStatus: workItem.type !== 'job' ? newStatus : undefined });

    // Undo everything the optimistic path did. Shared by the request failing
    // outright and by the orchestrator reporting a failure later.
    const revert = () => {
      if (isResolved) {
        resolvedTodayRef.current.delete(workItem.guid);
      }
      if (wasResolved && !isResolved) {
        // Put it back as a resolved snapshot so it reappears under "Resolved Today"
        resolvedTodayRef.current.set(workItem.guid, { ...workItem, status: prevStatus });
        removeWorkItem(workItem.guid);
      }
      updateWorkItem(workItem.guid, { status: prevStatus, appointmentStatus: workItem.type !== 'job' ? prevStatus : undefined });
    };

    const ticketId = workItem.id || workItem.guid;
    updateWorkItemStatus(workItem.guid, ticketId, newStatus, workItem.type)
      .then(({ messageId }) => {
        if (!messageId) return;

        // The 202 only means "queued". RepairShopr can still refuse the change
        // — most often a worksheet that has to be completed before a ticket may
        // be resolved — and that refusal arrives well after the POST resolved.
        watchJobStatusResult(messageId, {
          onFailure: (reason) => {
            console.error('[Dashboard] status change rejected — reverting:', reason);
            revert();
            setActionError({
              heading: `Couldn’t set ${describeWorkItem(workItem)} to “${newStatus}”`,
              reason,
            });
          },
        });
      })
      .catch((err) => {
        console.error('[Dashboard] updateWorkItemStatus FAILED — reverting', err);
        revert();
      });
  }, [updateWorkItem, removeWorkItem, restoreWorkItem]);

  const handleDelete = useCallback(async (workItem: WorkItem) => {
    // Check-in sessions are soft-cancelled (audit trail retained) rather
    // than hard-deleted. The generic /api/workitems/{guid} delete endpoint
    // doesn't know about cr001_check_in_sessionses — it 400s because no
    // matching job/appointment row exists for that GUID.
    //
    // We open a styled cancellation dialog (CancelSessionDialog) and
    // perform the actual cancel + optimistic update in its onConfirm
    // handler below.
    if (workItem.type === 'check-in') {
      const sessionGuid = workItem.checkInSessionGuid ?? workItem.guid;
      setPendingCancelCheckIn({ sessionGuid, workItemGuid: workItem.guid });
      return;
    }

    // Jobs go through the delete dialog: it lists what the delete removes and
    // leaves, refuses while an invoice has payments, and only then deletes.
    // The row is removed once the server confirms (handleJobDeleted).
    if (workItem.type === 'job') {
      setPendingDeleteJob(workItem);
      return;
    }

    const removedItem = removeWorkItem(workItem.guid);
    // Also remove from resolved-today sources so the item disappears immediately
    resolvedTodayRef.current.delete(workItem.guid);
    setResolvedTodayItems(prev => prev.filter(w => w.guid !== workItem.guid));

    try {
      await deleteWorkItem(workItem.guid, workItem.id, workItem.type);
    } catch (err) {
      console.error('[Dashboard] deleteWorkItem FAILED — restoring', err);
      if (removedItem) restoreWorkItem(removedItem);
      throw err;
    }
  }, [removeWorkItem, restoreWorkItem]);

  const handleJobDeleted = useCallback((guid: string) => {
    removeWorkItem(guid);
    resolvedTodayRef.current.delete(guid);
    setResolvedTodayItems(prev => prev.filter(w => w.guid !== guid));
  }, [removeWorkItem]);

  const handleEdit = useCallback((workItem: WorkItem) => {
    sessionStorage.setItem('edit_workitem_json', JSON.stringify(workItem));
    onNavigate?.('edit-ticket');
  }, [onNavigate]);

  // Open the RepairShopr print view for the ticket in a new tab. workItem.id is
  // the RepairShopr ticket id (same id used for the /tickets/{id} link); the
  // browser sends the GET using the tech's existing RS session.
  const handlePrintTicket = useCallback((workItem: WorkItem) => {
    window.open(
      `https://techsolvers.repairshopr.com/tickets/${workItem.id}/print`,
      '_blank',
      'noopener,noreferrer',
    );
  }, []);

  // Straight to the session's own URL. This used to hand the id off through
  // sessionStorage and flip MainLayout's view, which left the address bar on
  // `/` — so the session couldn't be linked, bookmarked or reloaded, and Back
  // walked out of the app instead of returning to the dashboard.
  const handleOpenCheckInSession = useCallback((sessionId: string) => {
    navigate(`/check-in/session/${sessionId}`);
  }, [navigate]);

  const handleConvertToJob = useCallback((workItem: WorkItem) => {
    const prefill = {
      customerId: workItem.customerId,
      customerName: workItem.customerName,
      customerBusinessName: workItem.customerBusinessName,
      title: workItem.title,
      description: workItem.address || workItem.notes || '',
      appointmentType: workItem.appointmentType,
      sourceAppointmentGuid: workItem.guid,
    };
    sessionStorage.setItem('new_ticket_prefill', JSON.stringify(prefill));
    onNavigate?.('new-ticket');
  }, [onNavigate]);

  // Open the change-customer dialog for a job/ticket from its expanded panel.
  const handleChangeCustomer = useCallback((item: WorkItem) => {
    setChangeCustomerTarget(item);
  }, []);

  // Apply the reassigned customer optimistically. The back end also broadcasts
  // workitems:changed, so other dashboards patch in place; this covers the
  // acting client immediately (and is idempotent with the broadcast).
  const handleCustomerChanged = useCallback((updated: WorkItem) => {
    updateWorkItem(updated.guid, updated);
    markChanged(updated.guid);
    setChangeCustomerTarget(null);
  }, [updateWorkItem, markChanged]);

  // Client-side filter — merge in session-resolved items and API-fetched resolved items when toggle is on
  const searchedWorkItems = useMemo(() => {
    const snapshotMap = resolvedTodayRef.current;

    // Apply resolved snapshots: overlay status/resolvedAt onto items still in
    // workItems (covers the race where a SignalR "created" event reverts the
    // optimistic resolve), and inject items that the API has removed entirely.
    const overlaid = snapshotMap.size > 0
      ? workItems.map(w => {
          const snap = snapshotMap.get(w.guid);
          return snap ? { ...w, status: snap.status, resolvedAt: snap.resolvedAt } : w;
        })
      : workItems;

    if (!showResolvedToday) {
      const base = overlaid.filter(w => !['resolved', 'closed'].includes((w.status ?? '').toLowerCase()));
      if (!searchTerm.trim()) return base;
      const lower = searchTerm.toLowerCase();
      return base.filter(
        w =>
          w.title.toLowerCase().includes(lower) ||
          w.customerName.toLowerCase().includes(lower) ||
          (w.customerBusinessName ?? '').toLowerCase().includes(lower),
      );
    }

    // Merge session snapshots + API-fetched resolved items, deduplicating by guid
    const mergedGuids = new Set(overlaid.map(w => w.guid));
    const extras: WorkItem[] = [];

    // Session snapshots not already in active list
    for (const snap of snapshotMap.values()) {
      if (!mergedGuids.has(snap.guid)) {
        extras.push(snap);
        mergedGuids.add(snap.guid);
      }
    }

    // API-fetched resolved items not already present
    for (const item of resolvedTodayItems) {
      if (!mergedGuids.has(item.guid)) {
        extras.push(item);
        mergedGuids.add(item.guid);
      }
    }

    const base = [...overlaid, ...extras];

    if (!searchTerm.trim()) return base;
    const lower = searchTerm.toLowerCase();
    return base.filter(
      w =>
        w.title.toLowerCase().includes(lower) ||
        w.customerName.toLowerCase().includes(lower) ||
        (w.customerBusinessName ?? '').toLowerCase().includes(lower),
    );
  }, [workItems, searchTerm, showResolvedToday, resolvedTodayItems]);

  const withCustomerCount = useMemo(
    () => workItems.filter(w => w.deviceWithCustomer).length,
    [workItems],
  );
  const filteredWorkItems = useMemo(
    () => (showWithCustomer ? searchedWorkItems.filter(w => w.deviceWithCustomer) : searchedWorkItems),
    [searchedWorkItems, showWithCustomer],
  );

  // Group by assignee
  const grouped = useMemo(() => {
    const map: Record<string, WorkItem[]> = {};
    for (const w of filteredWorkItems) {
      const assignee = w.userFullName || 'Unassigned';
      (map[assignee] ??= []).push(w);
    }
    // Sort: today's appointments top, jobs middle (by displayIndex), future appointments bottom
    for (const [assignee, items] of Object.entries(map)) {
      map[assignee] = sortWorkItemsForDashboard(items, showResolvedToday);
    }
    return map;
  }, [filteredWorkItems, showResolvedToday]);

  const sortedAssignees = useMemo(
    () => sortAssignees(Object.keys(grouped), CURRENT_USER),
    [grouped],
  );

  const isAdmin = userInfo?.roles.includes('admin') ?? false;


  const formatAge = useCallback((cachedAt: number): string => {
    const diffMs = Date.now() - cachedAt;
    const mins = Math.floor(diffMs / 60_000);
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    return `${hours}h ${mins % 60}m`;
  }, []);

  const debugConfig = useMemo<DebugPanelConfig>(() => ({
    sections: [
      { id: 'workitems', label: 'Work Items' },
    ],
    hookStates: [
      {
        name: 'useWorkItemData',
        section: 'workitems',
        status: error ? 'error' : isLoading ? 'loading' : 'loaded',
        error,
        recordCount: workItems.length,
      },
    ],
    caches: [
      {
        name: 'Task Lists',
        section: 'workitems',
        type: 'memory+localStorage',
        getRecordCount: () => getTaskListsCacheMetadata()?.recordCount ?? 0,
        getAge: () => {
          const meta = getTaskListsCacheMetadata();
          return meta ? formatAge(meta.cachedAt) : null;
        },
        getSizeEstimate: () => {
          try {
            const raw = localStorage.getItem('task_lists_cache_v1');
            if (!raw) return '0 KB';
            return `${Math.round(raw.length / 1024)} KB`;
          } catch { return 'unknown'; }
        },
        getData: () => getTaskListsCacheMetadata(),
        clear: () => { invalidateTaskListsCache(); },
        refresh: async () => { await refreshTaskListsCache(); },
      },
    ],
    dataSections: [],
  }), [workItems.length, error, isLoading, formatAge]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          action={
            <IconButton size="small" onClick={refresh}>
              <RefreshIcon />
            </IconButton>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <>
      {/* Contextual toolbar — full-bleed (SecondaryToolbar cancels
          .main-content's 10px padding) so it sits flush under the nav. */}
      <SecondaryToolbar floating={FEATURE_TECH_CARD_V2}>
        <ToolbarSearch
          primary="filter"
          filterValue={searchTerm}
          onFilterChange={setSearchTerm}
          filterPlaceholder="Filter work items…"
          searchValue={workItemSearch}
          onSearchChange={setWorkItemSearch}
          onSearchSubmit={handleWorkItemSearch}
          searchPlaceholder="Search work items…"
          /* Phones / small tablets: the filter starts collapsed to its icon too, and
             ToolbarActionGroup goes icon-only at the same 768px. */
          collapseBelow={768}
        />
        <ToolbarSpacer />
        {(withCustomerCount > 0 || showWithCustomer) && (
          <Chip
            label={`Devices with customers (${withCustomerCount})`}
            color="warning"
            size="small"
            variant={showWithCustomer ? 'filled' : 'outlined'}
            onClick={() => setShowWithCustomer(prev => !prev)}
            sx={{ mr: 1 }}
          />
        )}
        <ToolbarDivider />
        <ToolbarActionGroup
          onNewTicket={() => onNavigate?.('new-ticket')}
          onNewAppointment={() => onNavigate?.('new-appointment' as ViewType)}
          onNewInvoice={() => setNewInvoiceOpen(true)}
          onNewCheckIn={() => onNavigate?.('new-check-in' as ViewType)}
        />
      </SecondaryToolbar>
      {actionError && (
        <Box sx={{ px: FEATURE_TECH_CARD_V2 ? 'var(--page-gutter, 16px)' : 2, pt: 1.5, maxWidth: 1600, mx: 'auto' }}>
          <Alert severity="error" onClose={() => setActionError(null)}>
            <AlertTitle sx={{ mb: 0.25 }}>{actionError.heading}</AlertTitle>
            {actionError.reason}
          </Alert>
        </Box>
      )}
      <Box
        sx={
          FEATURE_TECH_CARD_V2
            ? { p: '12px var(--page-gutter, 16px) 24px', maxWidth: 1600, mx: 'auto' }
            : { p: 2, maxWidth: 1600, mx: 'auto', overflow: 'hidden' }
        }
      >

      {/* Grouped work items */}
      {filteredWorkItems.length === 0 ? (
        <Typography variant="body1" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          {isLoading ? 'Loading work items, please wait...' : 'No work items found'}
        </Typography>
      ) : FEATURE_TECH_CARD_V2 ? (
        <TechnicianCardList
          assignees={sortedAssignees}
          groups={grouped}
          collapsed={collapsedGroups}
          onToggle={toggleGroup}
          onRefresh={refresh}
          onResolvedToday={() => setShowResolvedToday(prev => {
            if (!prev) fetchResolvedToday();
            return !prev;
          })}
          onMove={commitMove}
          onReassign={reassignToAssignee}
          onEditItem={handleEdit}
          onConvertToJob={handleConvertToJob}
          onDeleteItem={handleDelete}
          onStatusChange={handleStatusChange}
          getPartLines={(item) => partLinesByWorkitem[item.jobGUID ?? item.guid]}
          renderExpansion={(item) => (
            <WorkItemPanelContainer
              workItem={item}
              userName={CURRENT_USER}
              onWorkItemUpdated={updateWorkItem}
              onOpenCheckInSession={handleOpenCheckInSession}
              onDelete={handleDelete}
              onChangeCustomer={handleChangeCustomer}
              isAdmin={isAdmin}
            />
          )}
        />
      ) : (
        sortedAssignees.map(assignee => {
          const items = grouped[assignee];
          return (
            <Box
              key={assignee}
              sx={{ mb: 3 }}
              onDragOver={draggedId ? (e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                setDragOverId(`end::${assignee}`);
              } : undefined}
              onDragLeave={draggedId ? () => setDragOverId(null) : undefined}
              onDrop={draggedId ? (e) => handleDrop(e, `end::${assignee}`) : undefined}
            >
              <Box
                sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
              >
                <Box
                  onClick={() => toggleGroup(assignee)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', userSelect: 'none', flex: 1 }}
                >
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{
                      transform: collapsedGroups.has(assignee) ? 'rotate(-90deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease',
                    }}
                  />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {assignee}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({items.length})
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Tooltip title="Show today's resolved tickets at the top">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setShowResolvedToday(prev => {
                          if (!prev) fetchResolvedToday();
                          return !prev;
                        });
                      }}
                      color={showResolvedToday ? 'success' : 'default'}
                    >
                      <CheckCircleOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <IconButton size="small" onClick={refresh} title="Refresh">
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              <Divider sx={{ mb: 1 }} />
              <Collapse in={!collapsedGroups.has(assignee)}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {items.map(workItem => (
                  <WorkItemAccordionRow
                    key={workItem.guid}
                    workItem={workItem}
                    userName={CURRENT_USER}
                    isAdmin={isAdmin}
                    onWorkItemUpdated={updateWorkItem}
                    onStatusChange={handleStatusChange}
                    onDelete={handleDelete}
                    onEdit={handleEdit}
                    onConvertToJob={handleConvertToJob}
                    onPrintTicket={handlePrintTicket}
                    onOpenCheckInSession={handleOpenCheckInSession}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onDragEnd={handleDragEnd}
                    isDragOver={dragOverId === workItem.guid}
                    isDragging={draggedId === workItem.guid}
                    isHighlighted={changedGuids.has(workItem.guid)}
                    partLines={partLinesByWorkitem[workItem.jobGUID ?? workItem.guid]}
                  />
                ))}
                {/* Drop zone for placing items at the bottom of the group */}
                {draggedId && (
                  <Box
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.dataTransfer.dropEffect = 'move';
                      setDragOverId(`end::${assignee}`);
                    }}
                    onDragLeave={() => setDragOverId(null)}
                    onDrop={(e) => { e.stopPropagation(); handleDrop(e, `end::${assignee}`); }}
                    sx={{
                      height: 24,
                      borderRadius: 1,
                      border: dragOverId === `end::${assignee}` ? '2px dashed' : '2px dashed transparent',
                      borderColor: dragOverId === `end::${assignee}` ? 'primary.main' : 'transparent',
                      backgroundColor: dragOverId === `end::${assignee}` ? 'action.hover' : 'transparent',
                      transition: 'all 0.15s ease',
                    }}
                  />
                )}
              </Box>
              </Collapse>
            </Box>
          );
        })
      )}
      <DebugPanel config={debugConfig} />
      <NewInvoiceDialog
        open={newInvoiceOpen}
        onClose={() => setNewInvoiceOpen(false)}
      />
      {changeCustomerTarget && (
        <WorkItemChangeCustomerDialog
          open={!!changeCustomerTarget}
          workItem={changeCustomerTarget}
          onClose={() => setChangeCustomerTarget(null)}
          onChanged={handleCustomerChanged}
        />
      )}
      <JobDeleteDialog
        open={!!pendingDeleteJob}
        job={pendingDeleteJob}
        onClose={() => setPendingDeleteJob(null)}
        onDeleted={result => handleJobDeleted(result.guid)}
      />
      <CancelSessionDialog
        open={!!pendingCancelCheckIn}
        onClose={() => setPendingCancelCheckIn(null)}
        onConfirm={async reason => {
          if (!pendingCancelCheckIn) return;
          const { sessionGuid, workItemGuid } = pendingCancelCheckIn;
          const removedItem = removeWorkItem(workItemGuid);
          try {
            await cancelCheckInSession(sessionGuid, reason);
            setPendingCancelCheckIn(null);
          } catch (err) {
            if (removedItem) restoreWorkItem(removedItem);
            // Re-throw so the dialog's onConfirm surfaces the error inline
            throw err;
          }
        }}
      />
      </Box>
    </>
  );
};

export default WorkItemDashboard;
